/* [ START OF MODULE - VER 1.2 (src/hooks/useTosGate.js) ] */
/**
 * useTosGate — resolves whether the signed-in user has accepted the current
 * published Terms of Service FOR THE GIVEN APP.
 *
 * appKey partitions terms per application ('eye', 'rfp-pulse', ...). Returns
 * { loading, accepted, doc, refresh }. Fails OPEN on read errors or when no ToS
 * is published for the app (never lock users out on a transient failure); the
 * normal "published but not yet accepted" case blocks via accepted=false.
 *
 * VER 1.2 — 2026-07-03 — BUILD-2026.503 — RMI-2026-261: keyed on the stable user id
 *   (not the session object) and no longer forces a loading state on re-check. A
 *   token refresh (e.g. returning from the mobile camera/file picker, which
 *   backgrounds the PWA) no longer re-fires the gate and remounts the app, which
 *   was destroying in-progress mobile photo uploads.
 * VER 1.1 — 2026-07-02 — BUILD-2026.499 — RMI-2026-257: added appKey (per-app ToS).
 * VER 1.0 — 2026-07-02 — BUILD-2026.498 — RMI-2026-256: initial ToS gate.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function useTosGate(session, profile, appKey) {
  const [state, setState] = useState({ loading: true, accepted: false, doc: null });
  const hasSession = !!session;
  const uid = profile ? profile.id : null;

  const check = useCallback(async () => {
    if (!hasSession || !uid || !appKey) {
      setState({ loading: false, accepted: true, doc: null });
      return;
    }
    try {
      const { data: docs } = await supabase
        .from('legal_documents')
        .select('id, app_key, doc_type, version, title, body, effective_date')
        .eq('app_key', appKey)
        .eq('doc_type', 'tos')
        .eq('published', true)
        .order('version', { ascending: false })
        .limit(1);
      const doc = (docs && docs[0]) || null;
      if (!doc) {
        setState({ loading: false, accepted: true, doc: null });
        return;
      }
      const { data: acks } = await supabase
        .from('tos_acknowledgements')
        .select('id')
        .eq('user_id', uid)
        .eq('app_key', appKey)
        .eq('doc_type', 'tos')
        .eq('doc_version', doc.version)
        .limit(1);
      setState({ loading: false, accepted: Array.isArray(acks) && acks.length > 0, doc: doc });
    } catch (err) {
      console.error('[useTosGate] check failed, failing open:', err);
      setState({ loading: false, accepted: true, doc: null });
    }
  }, [hasSession, uid, appKey]);

  // Runs on mount and only when the user or app actually changes — NOT on token
  // refresh / object-ref churn. Does not force `loading` back to true on re-check,
  // so an in-progress action (e.g. a mobile photo upload that backgrounded the PWA
  // via the camera/file picker, then triggered a token refresh on resume) is never
  // interrupted by a full-app remount to the loading screen.
  // RMI-2026-591 (react-hooks/set-state-in-effect): `check` calls setState
  // synchronously on its early-return branch; deferring the call one tick
  // keeps that setState out of the effect's own synchronous execution.
  useEffect(() => {
    const t = setTimeout(check, 0);
    return () => clearTimeout(t);
  }, [check]);

  return { loading: state.loading, accepted: state.accepted, doc: state.doc, refresh: check };
}
/* [ END OF MODULE - VER 1.2 (src/hooks/useTosGate.js) ] */
